import { create } from 'zustand';
import { useAppStore } from './appStore';

export interface NoteFile {
  name: string;
  size?: number;
}

interface NotesState {
  open: boolean;
  files: NoteFile[];
  loadingFiles: boolean;
  selectedFile: NoteFile | null;
  content: string;
  loadingContent: boolean;
  saving: boolean;
  saved: boolean;
  saveError: string | null;
  creating: boolean;

  // Actions
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  loadFiles: () => Promise<void>;
  selectFile: (file: NoteFile) => Promise<void>;
  setContent: (content: string) => void;
  saveFile: () => Promise<void>;
  createNote: (name?: string) => Promise<void>;
  deleteNote: (file: NoteFile) => Promise<void>;
}

const LAST_FILE_KEY = 'notes-last-file';
const NOTES_API_BASE = '/api/notes';

function getPluginBaseUrl(): string {
  const gwUrl = useAppStore.getState().gatewayUrl;
  try {
    const u = new URL(gwUrl);
    const proto = u.protocol === 'wss:' ? 'https:' : 'http:';
    return `${proto}//${u.host}`;
  } catch {
    return window.location.origin;
  }
}

function getAuthHeaders(): Record<string, string> {
  const token = useAppStore.getState().gatewayToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function notesFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const base = getPluginBaseUrl();
  const resp = await fetch(`${base}${path}`, {
    ...init,
    headers: { ...getAuthHeaders(), ...(init?.headers ?? {}) },
  });
  const rawBody = await resp.text().catch(() => '');
  let body: unknown = null;
  if (rawBody) {
    try {
      body = JSON.parse(rawBody) as unknown;
    } catch {
      body = rawBody;
    }
  }
  if (!resp.ok) {
    const msg = (body && typeof body === 'object' && 'error' in body)
      ? String((body as { error: string }).error)
      : `API ${resp.status}`;
    throw new Error(msg);
  }
  return (body ?? {}) as T;
}

let saveTimer: number | null = null;

function clearSaveTimer() {
  if (saveTimer !== null) {
    window.clearTimeout(saveTimer);
    saveTimer = null;
  }
}

export const useNotesStore = create<NotesState>((set, get) => ({
  open: false,
  files: [],
  loadingFiles: false,
  selectedFile: null,
  content: '',
  loadingContent: false,
  saving: false,
  saved: false,
  saveError: null,
  creating: false,

  openPanel: () => {
    set({ open: true });
    void get().loadFiles().then(() => {
      // Try to reopen last edited file
      const lastFileName = localStorage.getItem(LAST_FILE_KEY);
      const { files } = get();
      if (lastFileName) {
        const found = files.find((f) => f.name === lastFileName);
        if (found) {
          void get().selectFile(found);
          return;
        }
      }
      // Otherwise open first file if available
      if (files.length > 0) {
        void get().selectFile(files[0]);
      }
    });
  },

  closePanel: () => {
    clearSaveTimer();
    set({ open: false });
  },

  togglePanel: () => {
    if (get().open) {
      get().closePanel();
    } else {
      get().openPanel();
    }
  },

  loadFiles: async () => {
    set({ loadingFiles: true });
    try {
      const response = await notesFetch<{ files: Array<{ name: string; type?: string; size?: number }> }>(
        NOTES_API_BASE,
      );
      const files: NoteFile[] = (response.files ?? [])
        .filter((f) => f.type !== 'directory' && (f.name.endsWith('.md') || f.name.endsWith('.txt')))
        .map((f) => ({ name: f.name, size: f.size }))
        .sort((a, b) => a.name.localeCompare(b.name));
      set({ files, loadingFiles: false });
    } catch (err) {
      console.warn('[notes] Failed to load files:', err);
      set({ files: [], loadingFiles: false });
    }
  },

  selectFile: async (file) => {
    clearSaveTimer();
    set({ selectedFile: file, loadingContent: true, content: '', saved: false, saveError: null });
    localStorage.setItem(LAST_FILE_KEY, file.name);
    try {
      const response = await notesFetch<{ content: string }>(
        `${NOTES_API_BASE}/${encodeURIComponent(file.name)}`,
      );
      set({ content: response.content ?? '', loadingContent: false });
    } catch (err) {
      console.warn('[notes] Failed to load file:', err);
      set({ content: '', loadingContent: false });
    }
  },

  setContent: (content) => {
    set({ content, saved: false, saveError: null });
    // Debounce auto-save: 2 seconds after last keystroke
    clearSaveTimer();
    saveTimer = window.setTimeout(() => {
      void get().saveFile();
    }, 2000);
  },

  saveFile: async () => {
    const { selectedFile, content } = get();
    if (!selectedFile) return;

    set({ saving: true, saveError: null });
    try {
      await notesFetch<{ ok: boolean }>(
        `${NOTES_API_BASE}/${encodeURIComponent(selectedFile.name)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content }),
        },
      );
      set({ saving: false, saved: true });
    } catch (err) {
      console.warn('[notes] Failed to save file:', err);
      set({ saving: false, saveError: (err as Error).message });
    }
  },

  createNote: async (name?: string) => {
    const fileName = name?.trim()
      ? (name.trim().endsWith('.md') ? name.trim() : `${name.trim()}.md`)
      : `note-${new Date().toISOString().slice(0, 10)}.md`;

    // Validate name (no path separators or traversal)
    if (fileName.includes('/') || fileName.includes('..')) {
      console.warn('[notes] Invalid file name:', fileName);
      return;
    }

    set({ creating: true });
    try {
      // Check if file already exists
      const { files } = get();
      if (files.find((f) => f.name === fileName)) {
        console.warn('[notes] File already exists:', fileName);
        set({ creating: false });
        // Select the existing file instead
        const existing = files.find((f) => f.name === fileName);
        if (existing) await get().selectFile(existing);
        return;
      }

      // Use gateway file API to create the file (PUT creates if not exists)
      const defaultContent = `# ${fileName.replace(/\.(md|txt)$/, '')}\n\n`;
      await notesFetch<{ ok: boolean }>(
        `${NOTES_API_BASE}/${encodeURIComponent(fileName)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: defaultContent }),
        },
      );
      await get().loadFiles();
      const { files: updatedFiles } = get();
      const created = updatedFiles.find((f) => f.name === fileName);
      if (created) {
        await get().selectFile(created);
      }
      set({ creating: false });
    } catch (err) {
      console.warn('[notes] Failed to create note:', err);
      set({ creating: false });
    }
  },

  deleteNote: async (file) => {
    try {
      // Use gateway file API to delete the file
      await notesFetch<{ ok: boolean }>(
        `${NOTES_API_BASE}/${encodeURIComponent(file.name)}`,
        { method: 'DELETE' },
      );
      const { selectedFile } = get();
      if (selectedFile?.name === file.name) {
        set({ selectedFile: null, content: '', saved: false });
        localStorage.removeItem(LAST_FILE_KEY);
      }
      await get().loadFiles();
      // Select first available file
      const { files } = get();
      if (!get().selectedFile && files.length > 0) {
        await get().selectFile(files[0]);
      }
    } catch (err) {
      console.warn('[notes] Failed to delete note:', err);
    }
  },
}));
